import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';

import { AdminAxios as Axios } from '../../../Assets/Axios/Axios';

import { useToastContext } from '../../../Context/ToastContext';

import { toCurrencyString, toLocalPhone } from '../../../Helpers';

import { FindService, GetCategories, AdminProfessionalServices } from '../../../Server/Query';
import {
  AdminUpdateServiceCategory,
  AdminCreateServicePackage,
  AdminUpdateServicePackage,
  AdminDeleteServicePackage,
} from '../../../Server/Mutation';

const EMPTY_PACKAGE = {
  id: null,
  name: '',
  price: '',
  features: '',
  description: '',
  isActive: true,
};

function ServiceModal({ serviceId, dialogProps }) {
  const { showToastMessage } = useToastContext();

  const [service, setService] = useState(null);
  const [categories, setCategories] = useState([]);
  const [usedCategoryIds, setUsedCategoryIds] = useState(new Set());
  const [packageForm, setPackageForm] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadService = useCallback(async () => {
    if (!serviceId) return;
    const { data, errors } = await Axios(FindService({ id: serviceId }));
    if (errors || !data?.Service) {
      showToastMessage('Failed to load service.', 'error');
      return;
    }
    setService(data.Service);

    // Categories the SAME professional already uses on OTHER services — these
    // are excluded so a professional can't hold two services per category.
    if (data.Service.professional_id) {
      const res = await Axios(
        AdminProfessionalServices({ id: data.Service.professional_id })
      );
      const others = (res.data?.Professional?.services ?? []).filter(
        s => s.id !== data.Service.id
      );
      setUsedCategoryIds(new Set(others.map(s => s.categoryId).filter(Boolean)));
    }
  }, [serviceId, showToastMessage]);

  useEffect(() => {
    loadService();
    Axios(GetCategories).then(({ data, errors }) => {
      if (errors || !data?.categories) return;
      setCategories(data.categories);
    });
  }, [loadService]);

  async function handleChangeCategory(categoryId) {
    const { errors } = await Axios(
      AdminUpdateServiceCategory({ id: service.id, categoryId })
    );
    if (errors) {
      // Backend enforces one service per (professional, category).
      showToastMessage(errors[0]?.message ?? 'Failed to change category.', 'error');
      return;
    }
    showToastMessage('Category updated.', 'success');
    await loadService();
  }

  async function handleSavePackage() {
    if (!packageForm.name.trim() || packageForm.price === '') {
      showToastMessage('Package name and price are required.', 'error');
      return;
    }
    const features = packageForm.features
      .split(',')
      .map(f => f.trim())
      .filter(Boolean);

    setBusy(true);
    try {
      const payload = {
        name: packageForm.name.trim(),
        price: packageForm.price,
        description: packageForm.description || null,
        features,
        isActive: packageForm.isActive,
      };
      const { errors } = await Axios(
        packageForm.id
          ? AdminUpdateServicePackage({ id: packageForm.id, ...payload })
          : AdminCreateServicePackage({ serviceId: service.id, ...payload })
      );
      if (errors) {
        showToastMessage(errors[0]?.message ?? 'Failed to save package.', 'error');
        return;
      }
      showToastMessage(`Package ${packageForm.id ? 'updated' : 'added'}.`, 'success');
      setPackageForm(null);
      await loadService();
    } finally {
      setBusy(false);
    }
  }

  async function handleDeletePackage() {
    const target = deleteTarget;
    setDeleteTarget(null);
    const { errors } = await Axios(AdminDeleteServicePackage({ id: target.id }));
    if (errors) {
      showToastMessage(errors[0]?.message ?? 'Failed to delete package.', 'error');
      return;
    }
    showToastMessage('Package deleted.', 'success');
    await loadService();
  }

  const packages = service?.packages ?? [];

  return (
    <Dialog {...dialogProps} maxWidth='md' fullWidth>
      <DialogContent sx={{ px: 4 }}>
        <IconButton onClick={dialogProps.onClose} sx={{ ml: 'auto', display: 'flex' }}>
          <CloseIcon />
        </IconButton>

        {service && (
          <>
            <Typography variant='h6' fontWeight={600}>
              {service.name}
            </Typography>
            <Typography variant='body2' color='text.secondary' mb={2}>
              {service.service_id}
            </Typography>

            <Box
              display='grid'
              gridTemplateColumns={{ sm: 'auto 1fr' }}
              rowGap={2}
              columnGap={2}
              alignItems='center'
              mb={2}
            >
              <Typography fontWeight={500}>Category</Typography>
              <TextField
                select
                size='small'
                value={service.category_id ?? ''}
                onChange={e => handleChangeCategory(e.target.value)}
                sx={{ maxWidth: 320 }}
                helperText='Categories already used by this professional are disabled.'
              >
                {categories.map(c => (
                  <MenuItem key={c.id} value={c.id} disabled={usedCategoryIds.has(c.id)}>
                    {c.name}
                    {usedCategoryIds.has(c.id) ? ' (already used)' : ''}
                  </MenuItem>
                ))}
              </TextField>

              <Typography fontWeight={500}>Hotline</Typography>
              <Typography>{service.hotline_number ? toLocalPhone(service.hotline_number) : 'Not available'}</Typography>

              <Typography fontWeight={500}>Description</Typography>
              <Typography>{service.description}</Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
              <Typography variant='h6' variantMapping={{ h6: 'h2' }}>
                Packages
              </Typography>
              <Button
                size='small'
                startIcon={<AddIcon />}
                onClick={() => setPackageForm({ ...EMPTY_PACKAGE })}
              >
                Add Package
              </Button>
            </Stack>

            {packages.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                No packages yet.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {packages.map(pkg => (
                  <Box key={pkg.id} sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.50' }}>
                    <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                      <Box>
                        <Typography fontWeight={600}>
                          {pkg.name} — {toCurrencyString(pkg.price)}
                          {!pkg.isActive && <Chip label='Inactive' size='small' sx={{ ml: 1 }} />}
                        </Typography>
                        {pkg.description && (
                          <Typography variant='body2' color='text.secondary'>
                            {pkg.description}
                          </Typography>
                        )}
                        {(pkg.features ?? []).length > 0 && (
                          <Stack spacing={0.25} mt={0.5}>
                            {pkg.features.map((f, i) => (
                              <Stack key={i} direction='row' alignItems='center' gap={0.5}>
                                <CheckCircleIcon fontSize='small' color='success' />
                                <Typography variant='body2'>{f}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        )}
                      </Box>
                      <Box>
                        <IconButton
                          size='small'
                          onClick={() =>
                            setPackageForm({
                              id: pkg.id,
                              name: pkg.name,
                              price: pkg.price,
                              features: (pkg.features ?? []).join(', '),
                              description: pkg.description ?? '',
                              isActive: pkg.isActive,
                            })
                          }
                        >
                          <EditIcon fontSize='small' />
                        </IconButton>
                        <IconButton size='small' color='error' onClick={() => setDeleteTarget(pkg)}>
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      {/* Add / edit package */}
      <Dialog open={Boolean(packageForm)} onClose={() => setPackageForm(null)} fullWidth maxWidth='sm'>
        <DialogTitle>{packageForm?.id ? 'Edit Package' : 'Add Package'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label='Name'
              required
              placeholder='e.g. Standard, Premium'
              value={packageForm?.name ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, name: e.target.value }))}
            />
            <TextField
              label='Price'
              type='number'
              required
              value={packageForm?.price ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, price: e.target.value }))}
            />
            <TextField
              label='Features'
              placeholder='Comma-separated'
              helperText='Separate each feature with a comma.'
              value={packageForm?.features ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, features: e.target.value }))}
            />
            <TextField
              label='Description'
              multiline
              minRows={2}
              value={packageForm?.description ?? ''}
              onChange={e => setPackageForm(f => ({ ...f, description: e.target.value }))}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={packageForm?.isActive ?? true}
                  onChange={e => setPackageForm(f => ({ ...f, isActive: e.target.checked }))}
                />
              }
              label='Active'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageForm(null)}>Cancel</Button>
          <Button variant='contained' onClick={handleSavePackage} disabled={busy}>
            {packageForm?.id ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete package</DialogTitle>
        <DialogContent>
          <Typography>Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color='error' variant='contained' onClick={handleDeletePackage}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

ServiceModal.propTypes = {
  serviceId: PropTypes.string,
  dialogProps: PropTypes.object,
};

export default ServiceModal;
